<template>
  <v-container fluid class="catalog-page pa-0">
    <v-dialog v-model="draftDialog" persistent max-width="680px">
      <v-card class="rounded-xl">
        <div class="dialog-header">
          <div>
            <div class="dialog-title">Open Draft Order Found</div>
            <div class="dialog-subtitle">
              This customer already has draft order(s). Continue one or start
              new.
            </div>
          </div>

          <v-btn icon small @click="draftDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-4">
          <v-list two-line>
            <v-list-item
              v-for="draft in draftOrders"
              :key="draft.id"
              class="draft-order-row"
              @click="continueDraft(draft)"
            >
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">
                  Draft #{{ draft.localId }}
                </v-list-item-title>

                <v-list-item-subtitle>
                  {{ draft.updatedAt | moment("MM/DD/YYYY hh:mm A") }}
                  · {{ draft.total | currency }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-btn small rounded color="primary" dark> Continue </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-divider />

        <v-card-actions class="dialog-actions">
          <v-spacer />
          <v-btn text @click="draftDialog = false">Cancel</v-btn>
          <v-btn rounded outlined color="primary" @click="startNewDraft">
            Start New Order
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="priceDialog" max-width="380px">
      <v-card class="qty-pad-card">
        <div class="qty-pad-header">
          <div>
            <div class="qty-pad-title">Suggest Price</div>
            <div class="qty-pad-product">
              {{ priceProduct ? priceProduct.name : "" }}
            </div>
            <div class="caption grey--text mt-1" v-if="priceProduct">
              Original: {{ getOriginalPrice(priceProduct) | currency }}
            </div>
          </div>

          <v-btn icon small @click="closePricePad">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="qty-pad-display">
          {{ manualPriceDisplay || "0" }}
        </div>

        <div class="qty-pad-grid price-pad-grid">
          <v-btn
            v-for="number in keypadNumbers"
            :key="'price-' + number"
            class="qty-pad-btn"
            depressed
            @click="pressPriceNumber(number)"
          >
            {{ number }}
          </v-btn>

          <v-btn class="qty-pad-btn danger" depressed @click="clearPrice">
            Clear
          </v-btn>

          <v-btn class="qty-pad-btn" depressed @click="pressPriceNumber(0)">
            0
          </v-btn>

          <v-btn class="qty-pad-btn" depressed @click="pressPriceDecimal">
            .
          </v-btn>

          <v-btn
            class="qty-pad-btn price-reset-btn"
            depressed
            @click="resetSuggestedPrice"
          >
            Reset
          </v-btn>

          <v-btn class="qty-pad-btn danger" depressed @click="backspacePrice">
            <v-icon>mdi-backspace-outline</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <v-card-actions class="qty-pad-actions">
          <v-btn
            text
            @click="resetSuggestedPrice"
            v-if="hasSuggestedPrice(priceProduct)"
          >
            Reset
          </v-btn>

          <v-spacer />

          <v-btn text @click="closePricePad">Cancel</v-btn>

          <v-btn color="primary" rounded dark large @click="saveSuggestedPrice">
            Save Price
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="customerDialog" persistent max-width="650px">
      <v-card class="rounded-xl">
        <div class="dialog-header">
          <div>
            <div class="dialog-title">Start Sales Rep Catalog</div>
            <div class="dialog-subtitle">
              Select the customer before opening the catalog.
            </div>
          </div>

          <v-btn icon small @click="cancel">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-4">
          <div class="field-label">Customer</div>
          <CustomerAutoComplete
            v-model="selectedCustomer"
            :initValue="initCustomer"
            :errorMessages="selectCustomerError"
          />

          <div class="field-label mt-4">Sales Rep</div>
          <SalesMan v-model="selectedUser" :initValue="initUser" />

          <div
            v-if="
              currentBusiness &&
              currentBusiness.metadata &&
              !currentBusiness.metadata.isSingleCustomerPricing
            "
            class="mt-4"
          >
            <div class="field-label">Price Tier</div>
            <v-select
              :items="tiers"
              item-text="name"
              item-value="id"
              v-model="Order.PriceTierId"
              label="Select Price Tier"
              outlined
              dense
              hide-details
              clearable
              @change="reloadProducts"
            />
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="dialog-actions">
          <v-spacer />
          <v-btn text @click="cancel">Cancel</v-btn>
          <v-btn
            color="primary"
            rounded
            dark
            :disabled="!selectedCustomer || !selectedCustomer.id"
            @click="startCatalog"
          >
            Start Catalog
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="catalogDialog"
      fullscreen
      persistent
      transition="dialog-bottom-transition"
    >
      <v-card class="catalog-shell">
        <div class="catalog-topbar">
          <div>
            <div class="catalog-title">
              Sales Rep Catalog
              <span v-if="selectedCustomer">· {{ selectedCustomer.name }}</span>
            </div>
            <div class="catalog-subtitle">
              {{ Order.OrderDetails.length }} item rows · {{ totalQty }} qty ·
              {{ orderTotal | currency }}

              <span v-if="autosaveLoading"> · Saving...</span>
              <span v-else-if="autosaveError" class="red--text">
                · Not saved</span
              >
              <span v-else-if="lastSavedAt">
                · Saved {{ lastSavedAt | moment("hh:mm A") }}
              </span>
            </div>
          </div>

          <div class="catalog-actions">
            <v-btn rounded outlined @click="closeCatalog"> Close </v-btn>

            <v-btn
              color="primary"
              rounded
              dark
              :loading="saving"
              :disabled="Order.OrderDetails.length === 0"
              @click="saveOrder"
            >
              {{ orderTotal | currency }} Save Order
            </v-btn>
          </div>
        </div>
        <div class="catalog-filter-bar compact-filter-bar">
          <v-text-field
            v-model="search"
            outlined
            dense
            clearable
            hide-details
            prepend-inner-icon="mdi-magnify"
            label="Search products"
            class="catalog-search"
          />

          <v-select
            v-model="productSort"
            :items="productSortOptions"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details
            label="Sort"
            class="catalog-sort"
          />
          <v-btn
            rounded
            outlined
            small
            class="catalog-note-btn"
            @click="openNoteDialog"
          >
            <v-icon left small>
              {{
                Order.note ? "mdi-note-check-outline" : "mdi-note-plus-outline"
              }}
            </v-icon>
            {{ Order.note ? "Edit Note" : "Add Order Note" }}
          </v-btn>
        </div>

        <div class="category-tabs" v-if="Categories.length">
          <v-slide-group show-arrows>
            <v-slide-item v-for="category in Categories" :key="category.id">
              <v-btn
                small
                rounded
                class="mr-2"
                :disabled="loading"
                :color="selectedCategory === category.id ? 'primary' : ''"
                :outlined="selectedCategory !== category.id"
                :class="{ 'category-loading-disabled': loading }"
                @click="selectCategory(category)"
              >
                {{ category.name }}
              </v-btn>
            </v-slide-item>
          </v-slide-group>
        </div>

        <div class="catalog-body">
          <div class="product-area" ref="productArea">
            <div v-if="loading" class="catalog-loading-overlay">
              <v-progress-circular indeterminate color="primary" size="42" />
              <div class="loading-title">Loading products...</div>
              <div class="loading-subtitle">
                Getting items for this category
              </div>
            </div>

            <div
              v-for="department in groupedCatalogProducts"
              :key="department.name"
              class="department-group"
            >
              <div class="department-title">
                {{ department.name }}
                <span>{{ department.count }} items</span>
              </div>

              <v-row dense>
                <v-col
                  v-for="product in department.products"
                  :key="product.id"
                  cols="6"
                  sm="4"
                  md="2"
                  lg="2"
                >
                  <CatalogProductCard
                    :product="product"
                    :qty="getQty(product)"
                    :shouldShowStock="true"
                    :allowWebImages="allowWebProductImages"
                    @add="add"
                    @minus="minus"
                    @open-qty="openQty"
                    @open-price="openPrice"
                    @image-click="openImage"
                  />
                </v-col>
              </v-row>
            </div>

            <div v-if="hasMoreProducts && !loading" class="text-center my-4">
              <v-btn rounded outlined color="primary" @click="loadMoreProducts">
                Load More Products
              </v-btn>
            </div>
          </div>
        </div>

        <div class="floating-cart-bar" v-if="Order.OrderDetails.length">
          <div>
            <div class="floating-cart-label">Cart</div>
            <div class="floating-cart-total">
              {{ totalQty }} items · {{ orderTotal | currency }}
            </div>
          </div>

          <v-btn color="primary" rounded dark @click="cartDrawer = true">
            <v-icon left small>mdi-cart-outline</v-icon>
            Review Cart
          </v-btn>
        </div>

        <CatalogCartDrawer
          v-model="cartDrawer"
          :items="Order.OrderDetails"
          :totalQty="totalQty"
          :total="orderTotal"
          :saving="saving"
          @add="add"
          @minus="minus"
          @open-qty="openQty"
          @remove="removeItem"
          @save="saveOrder"
        />
      </v-card>
    </v-dialog>

    <v-dialog v-model="noteDialog" max-width="520px">
      <v-card class="rounded-xl">
        <div class="dialog-header">
          <div>
            <div class="dialog-title">Order Note</div>
            <div class="dialog-subtitle">
              Add delivery instructions or internal notes for this order.
            </div>
          </div>

          <v-btn icon small @click="noteDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-4">
          <v-textarea
            v-model="orderNoteDraft"
            outlined
            rows="4"
            auto-grow
            counter="500"
            maxlength="500"
            label="Example: Deliver tomorrow morning"
            hide-details="auto"
          />
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn text @click="clearOrderNote" v-if="Order.note">
            Remove Note
          </v-btn>

          <v-spacer />

          <v-btn text @click="noteDialog = false"> Cancel </v-btn>

          <v-btn color="primary" rounded dark @click="saveOrderNote">
            Save Note
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ProductImagePreviewDialog
      v-model="imageDialog"
      :product="imageProduct"
    />

    <v-dialog v-model="qtyDialog" max-width="380px">
      <v-card class="qty-pad-card">
        <div class="qty-pad-header">
          <div>
            <div class="qty-pad-title">Enter Quantity</div>
            <div class="qty-pad-product">
              {{ currentProduct ? currentProduct.name : "" }}
            </div>
          </div>

          <v-btn icon small @click="closeQtyPad">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="qty-pad-display">
          {{ manualQtyDisplay || "0" }}
        </div>

        <div class="qty-pad-grid">
          <v-btn
            v-for="number in keypadNumbers"
            :key="'qty-' + number"
            class="qty-pad-btn"
            depressed
            @click="pressQtyNumber(number)"
          >
            {{ number }}
          </v-btn>

          <v-btn class="qty-pad-btn danger" depressed @click="clearQty">
            Clear
          </v-btn>

          <v-btn class="qty-pad-btn" depressed @click="pressQtyNumber(0)">
            0
          </v-btn>

          <v-btn class="qty-pad-btn danger" depressed @click="backspaceQty">
            <v-icon>mdi-backspace-outline</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <v-card-actions class="qty-pad-actions">
          <v-btn text @click="closeQtyPad">Cancel</v-btn>
          <v-spacer />
          <v-btn color="primary" rounded dark large @click="saveQty">
            Apply Qty
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import CustomerAutoComplete from "@/modules/Customer/components/AutoComplete";
import SalesMan from "@/modules/User/components/AutoComplete.vue";
import productService from "@/modules/Product/service.js";
import categoryService from "@/modules/Product/Category/service.js";
import priceTierService from "@/modules/Product/PriceTier/service.js";
import CatalogProductCard from "@/modules/Order/components/Catalog/CatalogProductCard.vue";
import CatalogCartDrawer from "@/modules/Order/components/Catalog/CatalogCartDrawer.vue";
import ProductImagePreviewDialog from "@/components/Image/ProductImagePreviewDialog.vue";
import orderService from "@/modules/Order/service.js";
import { mapGetters } from "vuex";
const CATALOG_PRODUCT_SORT_KEY = "catalogProductSort";

export default {
  name: "catalog-order-create",

  components: {
    CustomerAutoComplete,
    SalesMan,
    CatalogProductCard,
    CatalogCartDrawer,
    ProductImagePreviewDialog,
  },

  data() {
    return {
      pricePadDirty: false,
      priceDialog: false,
      priceProduct: null,
      manualPriceDisplay: "",
      noteDialog: false,
      orderNoteDraft: "",
      productSort: "default",
      productSortOptions: [
        { text: "Default", value: "default" },
        { text: "Price: Low to High", value: "price_asc" },
        { text: "Price: High to Low", value: "price_desc" },
      ],
      productLimit: 150,
      productOffset: 0,
      hasMoreProducts: false,
      localSaveTimer: null,
      draftOrders: [],
      draftDialog: false,
      loadingDrafts: false,
      draftOrderId: null,
      autosaveTimer: null,
      autosaveLoading: false,
      autosaveError: false,
      lastSavedAt: null,
      customerDialog: true,
      catalogDialog: false,
      initCustomer: null,
      productCache: {},
      initUser: null,
      qtyMap: {},
      cartDrawer: false,
      imageDialog: false,
      imageProduct: null,
      selectedCustomer: null,
      selectedUser: null,
      selectCustomerError: [],
      loading: false,
      saving: false,
      search: "",
      searchTimer: null,
      Categories: [],
      Products: [],
      selectedCategory: null,
      tiers: [],
      qtyDialog: false,
      currentProduct: null,
      manualQty: 0,
      manualQtyDisplay: "",
      keypadNumbers: [1, 2, 3, 4, 5, 6, 7, 8, 9],
      Order: {
        date: this.$moment().toISOString(),
        note: null,
        CustomerId: null,
        SalesRepUserId: null,
        PriceTierId: null,
        OrderDetails: [],
      },
      headers: [
        {
          text: "Product",
          value: "name",
          align: "left",
          sortable: true,
        },
        {
          text: "Stock",
          value: "balance",
          align: "center",
          sortable: true,
        },
        {
          text: "Price",
          value: "price",
          align: "right",
          sortable: true,
        },
        {
          text: "Qty",
          value: "qty",
          align: "center",
          sortable: false,
        },
      ],
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser", "currentBusiness"]),

    allowWebProductImages() {
      return !(
        this.currentBusiness &&
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.generateAiProductImage === false
      );
    },

    groupedCatalogProducts() {
      const departmentMap = {};

      this.Products.forEach((product) => {
        const departmentName =
          product.Department && product.Department.name
            ? product.Department.name
            : "Unassigned";

        if (!departmentMap[departmentName]) {
          departmentMap[departmentName] = [];
        }

        departmentMap[departmentName].push(product);
      });

      return Object.keys(departmentMap)
        .sort((a, b) => a.localeCompare(b))
        .map((departmentName) => {
          const products = this.sortProductsForDisplay(
            departmentMap[departmentName]
          );

          return {
            name: departmentName,
            count: products.length,
            products,
          };
        });
    },
    totalQty() {
      return this.Order.OrderDetails.reduce((sum, row) => {
        return sum + Number(row.qty || 0);
      }, 0);
    },

    orderTotal() {
      return this.Order.OrderDetails.reduce((sum, row) => {
        return sum + Number(row.total || 0);
      }, 0);
    },
  },

  watch: {
    productSort() {
      this.saveProductSort();
      this.productCache = {};
      this.productOffset = 0;
      this.hasMoreProducts = false;
      this.Products = [];

      if (this.catalogDialog) {
        this.getProducts(true);
      }
    },
    selectedCustomer(val) {
      if (!val || !val.id) return;

      this.Order.CustomerId = val.id;

      if (val.PriceTierId) {
        this.Order.PriceTierId = val.PriceTierId;
      }

      if (val.SalesRep) {
        this.initUser = val.SalesRep;
        this.selectedUser = val.SalesRep;
      } else {
        this.initUser = this.currentUser;
        this.selectedUser = this.currentUser;
      }
    },

    selectedUser(val) {
      if (val && val.id) {
        this.Order.SalesRepUserId = val.id;
      }
    },

    search() {
      clearTimeout(this.searchTimer);

      this.searchTimer = setTimeout(async () => {
        this.Products = [];
        this.scrollProductTop();
        this.productOffset = 0;
        this.hasMoreProducts = false;
        this.Products = [];
        await this.getProducts(true);
        this.scrollProductTop();
      }, 300);
    },
  },

  created() {
    this.initUser = this.currentUser;
    this.selectedUser = this.currentUser;
    this.loadProductSort();
    this.getTiers();
  },

  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
    }

    if (this.localSaveTimer) {
      clearTimeout(this.localSaveTimer);
    }

    this.saveLocalDraft();
  },

  methods: {
    getOriginalPrice(product) {
      if (!product) return 0;

      return Number(
        product.originalCatalogPrice ||
          product.metadata?.originalCatalogPrice ||
          product.price ||
          0
      );
    },

    hasSuggestedPrice(product) {
      if (!product) return false;

      const originalPrice = this.getOriginalPrice(product);
      const currentPrice = Number(product.price || 0);

      return (
        Number(currentPrice.toFixed(2)) !== Number(originalPrice.toFixed(2))
      );
    },
    openPrice(product) {
      const originalPrice = this.getOriginalPrice(product);

      if (!product.originalCatalogPrice) {
        this.$set(product, "originalCatalogPrice", originalPrice);
      }

      this.priceProduct = product;
      this.manualPriceDisplay = String(Number(product.price || 0).toFixed(2));
      this.pricePadDirty = false;
      this.priceDialog = true;
    },

    closePricePad() {
      this.priceDialog = false;
      this.priceProduct = null;
      this.manualPriceDisplay = "";
      this.pricePadDirty = false;
    },

    pressPriceNumber(number) {
      const value = String(number);

      if (!this.pricePadDirty) {
        this.manualPriceDisplay = value;
        this.pricePadDirty = true;
        return;
      }

      if (this.manualPriceDisplay.length >= 10) return;

      if (this.manualPriceDisplay === "0") {
        this.manualPriceDisplay = value;
      } else {
        this.manualPriceDisplay += value;
      }
    },

    pressPriceDecimal() {
      if (!this.pricePadDirty) {
        this.manualPriceDisplay = "0.";
        this.pricePadDirty = true;
        return;
      }

      if (this.manualPriceDisplay.includes(".")) return;

      this.manualPriceDisplay = this.manualPriceDisplay
        ? `${this.manualPriceDisplay}.`
        : "0.";
    },

    backspacePrice() {
      this.manualPriceDisplay = this.manualPriceDisplay.slice(0, -1);
    },
    clearPrice() {
      this.manualPriceDisplay = "";
      this.pricePadDirty = true;
    },
    saveSuggestedPrice() {
      if (!this.priceProduct) return;

      const suggestedPrice = Number(this.manualPriceDisplay || 0);

      if (!suggestedPrice || suggestedPrice <= 0) {
        this.$swal("Price", "Enter a valid price", "warning");
        return;
      }

      const originalPrice = this.getOriginalPrice(this.priceProduct);

      this.$set(this.priceProduct, "originalCatalogPrice", originalPrice);
      this.$set(this.priceProduct, "price", Number(suggestedPrice.toFixed(2)));

      const changed =
        Number(suggestedPrice.toFixed(2)) !== Number(originalPrice.toFixed(2));

      this.$set(this.priceProduct, "priceChangedBySalesRep", changed);

      this.updateExistingDetailPrice(this.priceProduct);

      this.closePricePad();
    },

    resetSuggestedPrice() {
      if (!this.priceProduct) return;

      const originalPrice = this.getOriginalPrice(this.priceProduct);

      this.$set(this.priceProduct, "price", Number(originalPrice.toFixed(2)));
      this.$set(this.priceProduct, "priceChangedBySalesRep", false);

      this.updateExistingDetailPrice(this.priceProduct);

      this.closePricePad();
    },

    updateExistingDetailPrice(product) {
      const index = this.Order.OrderDetails.findIndex((row) => {
        return Number(row.ProductId) === Number(product.id);
      });

      if (index === -1) return;

      const qty = Number(this.Order.OrderDetails[index].qty || 0);
      const price = Number(product.price || 0);
      const originalPrice = this.getOriginalPrice(product);
      const changed =
        Number(price.toFixed(2)) !== Number(originalPrice.toFixed(2));

      const updated = {
        ...this.Order.OrderDetails[index],
        price,
        total: Number((price * qty).toFixed(2)),
        metadata: {
          ...(this.Order.OrderDetails[index].metadata || {}),
          originalPrice,
          suggestedPrice: changed ? price : null,
          priceChangedBySalesRep: changed,
          priceSuggestionStatus: changed ? "pending" : null,
          suggestedByUserId:
            changed && this.selectedUser ? this.selectedUser.id : null,
          suggestedByUserName:
            changed && this.selectedUser ? this.selectedUser.name : null,
        },
      };

      this.$set(this.Order.OrderDetails, index, updated);

      this.updateOrderPriceSuggestionMetadata();
      this.queueLocalSave();
      this.queueAutosave();
    },

    updateOrderPriceSuggestionMetadata() {
      const changedItems = this.Order.OrderDetails.filter((row) => {
        return row.metadata && row.metadata.priceChangedBySalesRep;
      });

      const metadata = {
        ...(this.Order.metadata || {}),
      };

      if (changedItems.length) {
        metadata.hasSalesRepPriceSuggestions = true;
        metadata.salesRepPriceSuggestionCount = changedItems.length;
      } else {
        delete metadata.hasSalesRepPriceSuggestions;
        delete metadata.salesRepPriceSuggestionCount;
      }

      this.$set(this.Order, "metadata", metadata);
    },
    openNoteDialog() {
      this.orderNoteDraft = this.Order.note || "";
      this.noteDialog = true;
    },

    saveOrderNote() {
      this.Order.note = String(this.orderNoteDraft || "").trim() || null;
      this.noteDialog = false;
      this.queueLocalSave();
      this.queueAutosave();
    },

    clearOrderNote() {
      this.Order.note = null;
      this.orderNoteDraft = "";
      this.noteDialog = false;
      this.queueLocalSave();
      this.queueAutosave();
    },
    loadProductSort() {
      const saved = localStorage.getItem(CATALOG_PRODUCT_SORT_KEY);

      if (saved) {
        this.productSort = saved;
      }
    },

    saveProductSort() {
      localStorage.setItem(
        CATALOG_PRODUCT_SORT_KEY,
        this.productSort || "default"
      );
    },

    getProductSortOrder() {
      if (this.productSort === "price_asc") {
        return {
          sortBy: ["price", "name"],
          sortDesc: [false, false],
        };
      }

      if (this.productSort === "price_desc") {
        return {
          sortBy: ["price", "name"],
          sortDesc: [true, false],
        };
      }

      return {
        sortBy: ["Department.name", "Brand.name", "name"],
        sortDesc: [false, false, false],
      };
    },

    sortProductsForDisplay(products) {
      const rows = products.slice();

      if (this.productSort === "price_asc") {
        return rows.sort((a, b) => {
          const priceCompare = Number(a.price || 0) - Number(b.price || 0);
          if (priceCompare !== 0) return priceCompare;

          return String(a.name || "").localeCompare(String(b.name || ""));
        });
      }

      if (this.productSort === "price_desc") {
        return rows.sort((a, b) => {
          const priceCompare = Number(b.price || 0) - Number(a.price || 0);
          if (priceCompare !== 0) return priceCompare;

          return String(a.name || "").localeCompare(String(b.name || ""));
        });
      }

      return rows.sort((a, b) => {
        const brandA = a.Brand && a.Brand.name ? a.Brand.name : "No Brand";
        const brandB = b.Brand && b.Brand.name ? b.Brand.name : "No Brand";

        const brandCompare = brandA.localeCompare(brandB);
        if (brandCompare !== 0) return brandCompare;

        return String(a.name || "").localeCompare(String(b.name || ""));
      });
    },
    async loadMoreProducts() {
      if (this.loading) return;

      this.productOffset += this.productLimit;
      await this.getProducts(true);
    },
    getLocalDraftKey() {
      const customerId =
        this.selectedCustomer && this.selectedCustomer.id
          ? this.selectedCustomer.id
          : "no_customer";

      return `${process.env.VUE_APP_STORAGE_PREFIX}catalogDraft_${customerId}`;
    },

    saveLocalDraft() {
      if (!this.selectedCustomer || !this.selectedCustomer.id) return;

      const payload = {
        draftOrderId: this.draftOrderId,
        selectedCustomer: this.selectedCustomer,
        selectedUser: this.selectedUser,
        Order: this.Order,
        qtyMap: this.qtyMap,
        selectedCategory: this.selectedCategory,
        search: this.search,
        savedAt: this.$moment().toISOString(),
      };

      localStorage.setItem(this.getLocalDraftKey(), JSON.stringify(payload));
    },

    queueLocalSave() {
      if (this.localSaveTimer) {
        clearTimeout(this.localSaveTimer);
      }

      this.localSaveTimer = setTimeout(() => {
        this.saveLocalDraft();
      }, 300);
    },

    restoreLocalDraft() {
      if (!this.selectedCustomer || !this.selectedCustomer.id) return false;

      const saved = localStorage.getItem(this.getLocalDraftKey());

      if (!saved) return false;

      try {
        const parsed = JSON.parse(saved);

        this.draftOrderId = parsed.draftOrderId || null;
        this.selectedCustomer =
          parsed.selectedCustomer || this.selectedCustomer;
        this.selectedUser = parsed.selectedUser || this.selectedUser;
        this.Order = parsed.Order || this.Order;
        this.qtyMap = parsed.qtyMap || {};
        this.selectedCategory =
          parsed.selectedCategory || this.selectedCategory;
        this.search = parsed.search || "";

        return true;
      } catch (e) {
        localStorage.removeItem(this.getLocalDraftKey());
        return false;
      }
    },

    clearLocalDraft() {
      if (!this.selectedCustomer || !this.selectedCustomer.id) return;
      localStorage.removeItem(this.getLocalDraftKey());
    },
    scrollProductTop() {
      this.$nextTick(() => {
        if (this.$refs.productArea) {
          this.$refs.productArea.scrollTop = 0;
        }
      });
    },
    async startCatalog() {
      this.selectCustomerError = [];

      if (!this.selectedCustomer || !this.selectedCustomer.id) {
        this.selectCustomerError = ["Please select customer"];
        return;
      }

      const restored = this.restoreLocalDraft();

      if (restored) {
        await this.openCatalog();
        return;
      }

      await this.checkCustomerDrafts();

      if (this.draftOrders.length > 0) {
        this.draftDialog = true;
        return;
      }

      await this.openCatalog();
    },
    queueAutosave() {
      if (!this.selectedCustomer || !this.selectedCustomer.id) return;
      if (!this.Order.OrderDetails.length) return;

      if (this.autosaveTimer) {
        clearTimeout(this.autosaveTimer);
      }

      this.autosaveTimer = setTimeout(() => {
        this.autosaveDraft();
      }, 1500);
    },

    async autosaveDraft() {
      if (!this.Order.OrderDetails.length) return;

      this.autosaveLoading = true;
      this.autosaveError = false;

      try {
        const response = await orderService.autosaveDraft({
          id: this.draftOrderId,
          ...this.Order,
          status: "draft",
          source: "catalog",
        });

        this.draftOrderId = response.data.id;
        this.lastSavedAt = this.$moment().toISOString();
        this.autosaveError = false;
      } catch (error) {
        console.error("autosave draft failed", error);
        this.autosaveError = true;
      } finally {
        this.autosaveLoading = false;
      }
    },
    async createServerDraft() {
      if (!this.Order.OrderDetails.length) return null;

      this.autosaveLoading = true;
      this.autosaveError = false;

      try {
        const response = await orderService.autosaveDraft({
          id: this.draftOrderId,
          ...this.Order,
          status: "draft",
          source: "catalog",
        });

        this.draftOrderId = response.data.id;
        this.lastSavedAt = this.$moment().toISOString();

        return response.data;
      } catch (error) {
        this.autosaveError = true;
        throw error;
      } finally {
        this.autosaveLoading = false;
      }
    },
    getProductCacheKey() {
      return [
        String(this.selectedCategory || "all"),
        String(this.search || ""),
        String(this.Order.PriceTierId || "no-tier"),
        String(this.productSort || "default"),
      ].join("|");
    },
    openImage(product) {
      this.imageProduct = product;
      this.$nextTick(() => {
        this.imageDialog = true;
      });
    },

    async checkCustomerDrafts() {
      this.loadingDrafts = true;

      try {
        const response = await orderService.getAll({
          CustomerId: this.selectedCustomer.id,
          status: "draft",
          source: "catalog",
          includeDraft: true,
          limit: 10,
          offset: 0,
        });

        this.draftOrders = response.data || [];
      } catch (error) {
        console.error("check customer drafts failed", error);
        this.draftOrders = [];
      } finally {
        this.loadingDrafts = false;
      }
    },

    async continueDraft(draft) {
      try {
        const response = await orderService.getById(draft.id);
        const order = response.data;

        this.draftOrderId = order.id;

        this.Order = {
          date: order.date || this.$moment().toISOString(),
          note: order.note || null,
          CustomerId: order.CustomerId,
          SalesRepUserId: order.SalesRepUserId,
          PriceTierId: order.PriceTierId,
          metadata: order.metadata || {},
          OrderDetails: [],
        };

        this.qtyMap = {};

        (order.OrderDetails || []).forEach((detail) => {
          const product = detail.Product || {};

          const row = {
            ProductId: detail.ProductId,
            Product: {
              ...product,
              id: detail.ProductId,
              name: detail.name || product.name,
              price: Number(detail.price || 0),
              imageUrl: detail.imageUrl || product.imageUrl,
              unitCountInBox: detail.unitCountInBox,
              originalCatalogPrice:
                detail.metadata && detail.metadata.originalPrice
                  ? Number(detail.metadata.originalPrice)
                  : Number(detail.price || 0),
              priceChangedBySalesRep:
                detail.metadata && detail.metadata.priceChangedBySalesRep,
            },
            name: detail.name || product.name,
            price: Number(detail.price || 0),
            qty: Number(detail.qty || 0),
            boxQty: Number(detail.qty || 0),
            unitCountInBox: detail.unitCountInBox,
            imageUrl: detail.imageUrl || product.imageUrl,
            total: Number(
              detail.total ||
                detail.totalPrice ||
                Number(detail.qty || 0) * Number(detail.price || 0)
            ),
          };

          this.Order.OrderDetails.push(row);
          this.$set(this.qtyMap, detail.ProductId, row.qty);
        });

        this.lastSavedAt = order.updatedAt || this.$moment().toISOString();
        this.autosaveError = false;
        this.draftDialog = false;

        await this.openCatalog();
      } catch (error) {
        console.error("continue draft failed", error);
        this.$swal("Draft Order", "Unable to open draft order", "error");
      }
    },

    async startNewDraft() {
      this.draftOrderId = null;
      this.qtyMap = {};
      this.Order = {
        date: this.$moment().toISOString(),
        note: null,
        CustomerId: this.selectedCustomer ? this.selectedCustomer.id : null,
        SalesRepUserId: this.selectedUser ? this.selectedUser.id : null,
        PriceTierId: this.selectedCustomer
          ? this.selectedCustomer.PriceTierId || null
          : null,
        metadata: {},
        OrderDetails: [],
      };
      this.lastSavedAt = null;
      this.autosaveError = false;
      this.draftDialog = false;
      this.clearLocalDraft();

      await this.openCatalog();
    },

    async openCatalog() {
      this.customerDialog = false;
      this.catalogDialog = true;

      await this.getCategories();
      await this.getProducts();
    },
    async getTiers() {
      try {
        const response = await priceTierService.getAll();
        this.tiers = response.data || [];
      } catch (error) {
        console.error("get tiers failed", error);
        this.tiers = [];
      }
    },
    async getCategories() {
      try {
        const response = await categoryService.getAll({
          remoteOrder: true,
        });

        const categories = (response.data || []).slice().sort((a, b) => {
          const aOrder = parseInt(a.sortOrder, 10);
          const bOrder = parseInt(b.sortOrder, 10);

          const aHasOrder = !isNaN(aOrder) && aOrder > 0;
          const bHasOrder = !isNaN(bOrder) && bOrder > 0;

          if (aHasOrder && bHasOrder) {
            return aOrder - bOrder;
          }

          if (aHasOrder) return -1;
          if (bHasOrder) return 1;

          return String(a.name || "").localeCompare(String(b.name || ""));
        });

        const hasNewProduct = categories.some((row) => {
          return String(row.name || "").toLowerCase() === "new product";
        });

        const hasSpecialPrices = categories.some((row) => {
          return String(row.name || "").toLowerCase() === "special prices";
        });

        if (
          !hasNewProduct &&
          !(
            this.currentBusiness &&
            this.currentBusiness.metadata &&
            this.currentBusiness.metadata.hideNewProductTabOnOrder
          )
        ) {
          categories.unshift({
            id: "new_product",
            name: "New Product",
          });
        }

        if (
          !hasSpecialPrices &&
          !(
            this.currentBusiness &&
            this.currentBusiness.metadata &&
            this.currentBusiness.metadata.hideSpecialProductTabOnOrder
          )
        ) {
          categories.unshift({
            id: "special_prices",
            name: "Special Prices",
          });
        }

        this.Categories = categories;

        if (this.Categories.length) {
          this.selectedCategory = this.Categories[0].id;
        }
      } catch (error) {
        console.error("get categories failed", error);
        this.Categories = [];
      }
    },

    async getProducts(forceRefresh = false) {
      const cacheKey = this.getProductCacheKey();

      if (!forceRefresh && this.productCache[cacheKey]) {
        this.Products = this.productCache[cacheKey].map((product) => {
          return {
            ...product,
            orderQuantity: this.getQty(product),
          };
        });
        return;
      }

      this.loading = true;

      try {
        const selectedCategoryObj = this.Categories.find((row) => {
          return String(row.id) === String(this.selectedCategory);
        });

        const selectedName =
          selectedCategoryObj && selectedCategoryObj.name
            ? String(selectedCategoryObj.name).toLowerCase().trim()
            : "";

        const sortOrder = this.getProductSortOrder();

        const where = {
          hideParent: true,
          remoteOrder: true,
          limit: this.productLimit,
          offset: this.productOffset,
          search: this.search,
          sortBy: sortOrder.sortBy,
          sortDesc: sortOrder.sortDesc,
        };

        if (
          this.currentBusiness &&
          this.currentBusiness.metadata &&
          this.currentBusiness.metadata.showOutOfStockItems
        ) {
          where.hideZero = false;
        } else {
          where.hideZero = true;
        }

        const hasSearch = String(this.search || "").trim().length > 0;

        if (!hasSearch) {
          if (
            this.selectedCategory === "special_prices" ||
            selectedName === "special prices" ||
            selectedName === "special price"
          ) {
            where.isSpecial = true;
          } else if (
            this.selectedCategory === "new_product" ||
            selectedName === "new product" ||
            selectedName === "new products"
          ) {
            where.isNew = true;
          } else if (this.selectedCategory) {
            where.CategoryId = this.selectedCategory;
          }
        }

        const response = await productService.getAll(where);
        let products = response.data || [];

        this.hasMoreProducts = products.length === this.productLimit;

        products = products.map((product) => {
          return {
            ...product,
            orderQuantity: this.getQty(product),
          };
        });

        if (this.productOffset > 0) {
          this.Products = [...this.Products, ...products];
        } else {
          this.Products = products;
        }

        await this.applyTierPrices();

        this.$set(this.productCache, cacheKey, this.Products);
      } catch (error) {
        console.error("get products failed", error);
        this.Products = [];
      } finally {
        this.loading = false;
      }
    },

    async selectCategory(category) {
      if (this.loading) return;

      this.selectedCategory = category.id;
      this.productOffset = 0;
      this.hasMoreProducts = false;
      this.Products = [];

      this.queueLocalSave();
      this.scrollProductTop();

      await this.getProducts(true);

      this.scrollProductTop();
    },
    async applyTierPrices() {
      if (
        this.currentBusiness &&
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.isSingleCustomerPricing
      ) {
        return;
      }

      if (!this.Order.PriceTierId) return;

      try {
        const response = await priceTierService.getById(this.Order.PriceTierId);
        const tier = response.data;

        if (!tier) return;

        this.Products = this.Products.map((product) => {
          let price = Number(product.price || 0);

          if (tier.discountType === "percentage" && tier.rate) {
            price = price - (price * Number(tier.rate || 0)) / 100;
          }

          if (
            tier.discountType === "fixed" &&
            Array.isArray(tier.ProductPrices)
          ) {
            const tierPrice = tier.ProductPrices.find((row) => {
              return Number(row.ProductId) === Number(product.id);
            });

            if (tierPrice && tierPrice.price != null) {
              price = Number(tierPrice.price);
            }
          }

          return {
            ...product,
            price: Number(price.toFixed(2)),
          };
        });
      } catch (error) {
        console.error("apply tier prices failed", error);
      }
    },

    async reloadProducts() {
      this.productCache = {};
      await this.getProducts(true);
      this.recalculateCartPrices();
    },

    // async selectCategory(category) {
    //   if (this.loading) return;

    //   this.selectedCategory = category.id;
    //   this.scrollProductTop();

    //   const cacheKey = this.getProductCacheKey();

    //   if (this.productCache[cacheKey]) {
    //     this.Products = this.productCache[cacheKey].map((product) => {
    //       return {
    //         ...product,
    //         orderQuantity: this.getQty(product),
    //       };
    //     });

    //     this.scrollProductTop();
    //     return;
    //   }

    //   this.Products = [];
    //   this.scrollProductTop();

    //   await this.getProducts();
    //   this.scrollProductTop();
    // },
    findDetail(productId) {
      return this.Order.OrderDetails.find((row) => {
        return Number(row.ProductId) === Number(productId);
      });
    },

    getQty(product) {
      return Number(this.qtyMap[product.id] || 0);
    },

    add(product) {
      const currentQty = this.getQty(product);
      this.setQty(product, currentQty + 1);
    },

    minus(product) {
      const currentQty = this.getQty(product);
      if (currentQty <= 0) return;
      this.setQty(product, currentQty - 1);
    },

    openQty(product) {
      this.currentProduct = product;

      const currentQty = this.getQty(product);

      this.manualQty = currentQty || 0;
      this.manualQtyDisplay = currentQty ? String(currentQty) : "";

      this.qtyDialog = true;
    },

    pressQtyNumber(number) {
      const value = String(number);

      if (this.manualQtyDisplay.length >= 5) return;

      if (this.manualQtyDisplay === "0") {
        this.manualQtyDisplay = value;
      } else {
        this.manualQtyDisplay += value;
      }

      this.manualQty = Number(this.manualQtyDisplay || 0);
    },

    backspaceQty() {
      this.manualQtyDisplay = this.manualQtyDisplay.slice(0, -1);
      this.manualQty = Number(this.manualQtyDisplay || 0);
    },

    clearQty() {
      this.manualQtyDisplay = "";
      this.manualQty = 0;
    },

    closeQtyPad() {
      this.qtyDialog = false;
      this.currentProduct = null;
      this.manualQty = 0;
      this.manualQtyDisplay = "";
    },

    saveQty() {
      if (!this.currentProduct) return;

      const qty = Number(this.manualQtyDisplay || 0);

      this.setQty(this.currentProduct, qty);

      this.closeQtyPad();
    },
    setQty(product, qty) {
      qty = Number(qty || 0);

      this.$set(this.qtyMap, product.id, qty);
      this.$set(product, "orderQuantity", qty);

      const index = this.Order.OrderDetails.findIndex((row) => {
        return Number(row.ProductId) === Number(product.id);
      });

      if (qty <= 0) {
        if (index !== -1) {
          this.Order.OrderDetails.splice(index, 1);
        }
        this.updateOrderPriceSuggestionMetadata();
        this.queueLocalSave();
        this.queueAutosave();
        return;
      }

      const price = Number(product.price || 0);
      const total = Number((price * qty).toFixed(2));

      const originalPrice = this.getOriginalPrice(product);
      const priceChangedBySalesRep = this.hasSuggestedPrice(product);

      const detail = {
        ProductId: product.id,
        Product: product,
        name: product.name,
        price,
        qty,
        boxQty: qty,
        unitCountInBox: product.unitCountInBox,
        imageUrl: product.imageUrl,
        total,
        metadata: {
          originalPrice,
          suggestedPrice: priceChangedBySalesRep ? price : null,
          priceChangedBySalesRep,
          priceSuggestionStatus: priceChangedBySalesRep ? "pending" : null,
          suggestedByUserId:
            priceChangedBySalesRep && this.selectedUser
              ? this.selectedUser.id
              : null,
          suggestedByUserName:
            priceChangedBySalesRep && this.selectedUser
              ? this.selectedUser.name
              : null,
        },
      };

      if (index === -1) {
        this.Order.OrderDetails.push(detail);
      } else {
        this.$set(this.Order.OrderDetails, index, detail);
      }

      this.updateOrderPriceSuggestionMetadata();
      this.queueLocalSave();
      this.queueAutosave();
    },

    updateProductQty(productId, qty) {
      this.$set(this.qtyMap, productId, qty);

      const product = this.Products.find((row) => {
        return Number(row.id) === Number(productId);
      });

      if (product) {
        this.$set(product, "orderQuantity", qty);
      }
    },

    removeItem(index) {
      const detail = this.Order.OrderDetails[index];
      this.Order.OrderDetails.splice(index, 1);

      if (detail) {
        this.updateProductQty(detail.ProductId, 0);
      }

      this.queueLocalSave();
      this.queueAutosave();
    },

    recalculateCartPrices() {
      this.Order.OrderDetails = this.Order.OrderDetails.map((detail) => {
        const product = this.Products.find((row) => {
          return Number(row.id) === Number(detail.ProductId);
        });

        if (!product) return detail;

        const price = Number(product.price || 0);
        const qty = Number(detail.qty || 0);

        return {
          ...detail,
          price,
          total: Number((price * qty).toFixed(2)),
        };
      });
    },

    async saveOrder() {
      if (!this.Order.OrderDetails.length) return;

      this.saving = true;

      try {
        if (this.autosaveTimer) {
          clearTimeout(this.autosaveTimer);
          this.autosaveTimer = null;
        }

        if (!this.draftOrderId) {
          await this.createServerDraft();
        }

        await orderService.autosaveDraft({
          id: this.draftOrderId,
          ...this.Order,
          status: "draft",
          source: "catalog",
        });

        await orderService.submitDraft(this.draftOrderId);

        this.lastSavedAt = this.$moment().toISOString();
        this.autosaveError = false;

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: "Order was submitted!",
          });
        this.clearLocalDraft();
        this.clearOrder();
        this.$router.push("/order/list");
      } catch (error) {
        this.$swal("Create Order", "Unable to submit order", "error");
      } finally {
        this.saving = false;
      }
    },
    clearOrder() {
      this.Order = {
        date: this.$moment().toISOString(),
        note: null,
        CustomerId: null,
        SalesRepUserId: null,
        PriceTierId: null,
        metadata: {},
        OrderDetails: [],
      };

      this.Products = [];
      this.selectedCategory = null;
      this.selectedCustomer = null;
      this.selectedUser = this.currentUser;
      this.draftOrderId = null;
      this.qtyMap = {};
      this.productCache = {};
      this.autosaveLoading = false;
      this.autosaveError = false;
      this.lastSavedAt = null;
    },
    closeCatalog() {
      this.$swal({
        title: "Close catalog?",
        text: "Current catalog items will stay on this screen until you leave.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Close",
        cancelButtonText: "Keep working",
      }).then((result) => {
        if (result.value) {
          this.catalogDialog = false;
          this.customerDialog = true;
        }
      });
    },

    cancel() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
.catalog-page {
  background: #f8fafc;
  min-height: 100%;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.dialog-actions {
  padding: 12px 16px;
}

.field-label {
  font-size: 12px;
  font-weight: 800;
  color: #475569;
  margin-bottom: 6px;
}

.catalog-shell {
  background: #f8fafc;
  height: 100vh;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.catalog-topbar {
  height: 54px;
  min-height: 54px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.catalog-title {
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
  line-height: 1.1;
}

.catalog-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.catalog-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.catalog-actions .v-btn {
  height: 34px !important;
}

.compact-filter-bar {
  min-height: 54px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 12px;
  display: flex;
  align-items: center;
}

.clickable-image {
  cursor: zoom-in;
}

.catalog-search {
  max-width: 420px;
}

.category-tabs {
  min-height: 48px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 12px;
}

.catalog-body {
  flex: 1;
  min-height: 0;
  padding: 10px 8px 86px;
  overflow: hidden;
}

.product-area {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px;
}

.department-title {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #f8fafc;
  padding: 8px 4px;
  font-size: 17px;
  font-weight: 900;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
}

.department-title span {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

.brand-title {
  font-size: 13px;
  font-weight: 900;
  color: #334155;
  background: #eaf0ff;
  border: 1px solid #dbe4ff;
  border-radius: 10px;
  padding: 7px 10px;
  margin: 8px 0;
}

.compact-product-card {
  border-radius: 14px;
  height: 100%;
  overflow: hidden;
}

.product-image,
.compact-no-image {
  height: 92px !important;
  background: #f8fafc;
}

.compact-product-content {
  padding: 8px !important;
}

.compact-product-name {
  font-size: 12px;
  font-weight: 900;
  line-height: 1.25;
  color: #0f172a;
  min-height: 46px;
  max-height: 46px;
  overflow: hidden;
}

.product-price-row {
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.product-price {
  font-size: 15px;
  font-weight: 900;
  color: #0f172a;
}

.touch-qty-row {
  margin-top: 8px;
  display: grid;
  grid-template-columns: 44px 1fr 44px;
  gap: 6px;
  align-items: center;
}

.touch-qty-btn {
  height: 40px !important;
  min-width: 40px !important;
  border-radius: 12px !important;
}

.minus-btn {
  background: #fee2e2 !important;
  color: #dc2626 !important;
}

.plus-btn {
  background: #facc15 !important;
  color: #0f172a !important;
}

.touch-qty-value {
  height: 40px;
  border: 1px solid #0f172a;
  border-radius: 12px;
  background: #ffffff;
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
}

.floating-cart-bar {
  position: fixed;
  left: 10px;
  right: 10px;
  bottom: 10px;
  z-index: 20;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.18);
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.floating-cart-total {
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
}

@media (max-width: 960px) {
  .catalog-title {
    font-size: 16px;
  }

  .catalog-subtitle {
    font-size: 11px;
  }

  .catalog-search {
    max-width: 100%;
  }

  .product-image,
  .compact-no-image {
    height: 86px !important;
  }

  .touch-qty-row {
    grid-template-columns: 42px 1fr 42px;
  }

  .touch-qty-btn,
  .touch-qty-value {
    height: 38px !important;
  }
}

@media (max-width: 600px) {
  .catalog-topbar {
    height: auto;
    min-height: 58px;
    align-items: flex-start;
  }

  .catalog-actions {
    flex-shrink: 0;
  }

  .catalog-actions .v-btn:first-child {
    display: none;
  }

  .compact-filter-bar {
    padding: 8px;
  }

  .category-tabs {
    padding: 7px 8px;
  }

  .catalog-body {
    padding: 8px 6px 84px;
  }

  .compact-product-name {
    font-size: 11px;
  }
}

.catalog-filter-bar {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.catalog-search {
  max-width: 480px;
}

.category-tabs {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 16px;
}

.catalog-body {
  flex: 1;
  min-height: 0;
  padding: 12px;
  padding-bottom: 92px;
}

.product-area {
  position: relative;
  overflow: auto;
  height: 100%;
}

.catalog-loading-overlay {
  position: sticky;
  top: 0;
  z-index: 8;
  min-height: 180px;
  background: rgba(248, 250, 252, 0.92);
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  backdrop-filter: blur(3px);
  margin-bottom: 12px;
}

.loading-title {
  font-size: 15px;
  font-weight: 900;
  color: #0f172a;
  margin-top: 10px;
}

.loading-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.category-loading-disabled {
  opacity: 0.45;
}

.department-group {
  margin-bottom: 20px;
}

.department-title {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f8fafc;
  padding: 10px 4px;
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
}

.department-title span {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

.brand-group {
  margin-bottom: 14px;
}

.brand-title {
  font-size: 13px;
  font-weight: 900;
  color: #475569;
  background: #eef2ff;
  border: 1px solid #dbe4ff;
  border-radius: 12px;
  padding: 7px 10px;
  margin-bottom: 8px;
}

.compact-product-card {
  border-radius: 16px;
}

.compact-no-image {
  height: 105px;
}

.compact-product-content {
  padding: 9px !important;
}

.compact-product-name {
  font-size: 12px;
  line-height: 1.25;
  min-height: 46px;
}

.compact-qty-row {
  gap: 6px;
}

.floating-cart-bar {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 20;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.18);
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.floating-cart-label {
  font-size: 11px;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
}

.floating-cart-total {
  font-size: 17px;
  font-weight: 900;
  color: #0f172a;
}

@media (max-width: 600px) {
  .catalog-body {
    padding: 8px;
    padding-bottom: 96px;
  }

  .department-title {
    font-size: 16px;
  }

  .brand-title {
    font-size: 12px;
  }

  .floating-cart-bar {
    left: 8px;
    right: 8px;
    bottom: 8px;
  }

  .floating-cart-bar .v-btn {
    min-width: 120px;
  }

  .floating-cart-total {
    font-size: 15px;
  }
}

.cart-title {
  padding: 14px;
  font-size: 18px;
  font-weight: 900;
  border-bottom: 1px solid #e2e8f0;
}

.product-image {
  background: #f8fafc;
}

.price-pad-grid {
  grid-template-columns: repeat(3, 1fr);
}

.price-reset-btn {
  grid-column: span 2;
  font-size: 15px !important;
  letter-spacing: 1px;
}

.no-image {
  height: 150px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

.product-content {
  padding: 12px;
}

.product-name {
  font-size: 14px;
  font-weight: 900;
  color: #0f172a;
  min-height: 42px;
}

.product-meta {
  font-size: 12px;
  color: #64748b;
  min-height: 18px;
}

.clickable-image {
  cursor: zoom-in;
}

.image-preview-card {
  overflow: hidden;
}

.image-preview-header {
  height: 52px;
  padding: 0 14px 0 18px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.image-preview-title {
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
  line-height: 1.2;
}

.product-price-row {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
}

.product-price {
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
}

.qty-btn {
  min-width: 64px;
}

.catalog-note-btn {
  height: 40px !important;
  flex: 0 0 auto;
}

.order-note-preview {
  background: #eff6ff;
  border-bottom: 1px solid #bfdbfe;
  color: #1e3a8a;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.order-note-preview span {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.table-product {
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-qty-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.centered-input input {
  text-align: center;
}

.qty-pad-card {
  border-radius: 16px;
  overflow: hidden;
}

.qty-pad-header {
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.qty-pad-title {
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
}

.qty-pad-product {
  font-size: 15px;
  color: #334155;
  line-height: 1.35;
  margin-top: 4px;
}

.qty-pad-display {
  height: 58px;
  padding: 8px 18px;
  font-size: 34px;
  font-weight: 900;
  color: #0f172a;
  text-align: right;
  border-bottom: 1px solid #e2e8f0;
}

.qty-pad-grid {
  padding: 14px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.qty-pad-btn {
  height: 58px !important;
  min-width: 0 !important;
  width: 100%;
  border-radius: 14px !important;
  font-size: 24px !important;
  font-weight: 900 !important;
  background: #f8fafc !important;
  color: #0f172a !important;
}

.qty-pad-btn.danger {
  font-size: 15px !important;
  letter-spacing: 1px;
}

.qty-pad-actions {
  padding: 12px 16px;
}

.qty-pad-actions .v-btn {
  min-height: 44px;
}

@media (max-width: 600px) {
  .catalog-topbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .catalog-actions {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .catalog-actions .v-btn {
    width: 100%;
  }

  .catalog-filter-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .catalog-search {
    max-width: 100%;
  }
}
</style>
